export type ThemeId =
  | "carbon"
  | "mono"
  | "paper"
  | "sand"
  | "citrus"
  | "midnight"
  | "nord"
  | "forest"
  | "gold"
  | "ember"
  | "oxblood"
  | "orchid"
  | "vapor"
  | "cyberpunk"
  | "prism"
  | "terminal";

export type Theme = {
  id: ThemeId;
  name: string;
  blurb: string;
  swatch: [string, string, string];
};

export const THEME_STORAGE_KEY = "momentum.theme";
export const DEFAULT_THEME: ThemeId = "prism";

export const THEMES: Theme[] = [
  {
    id: "prism",
    name: "Prism",
    blurb: "The default. Frosted keys under RGB light. Dark base, bright cyan and magenta glow.",
    swatch: ["#0D0C12", "#1A1822", "#00D9FF"],
  },
  {
    id: "carbon",
    name: "Stealth Carbon",
    blurb: "Matte black, no distractions.",
    swatch: ["#07080A", "#131518", "#C7CBD0"],
  },
  {
    id: "mono",
    name: "Pure Mono",
    blurb: "True black and white. Maximum contrast.",
    swatch: ["#030303", "#0D0D0D", "#FCFCFC"],
  },
  {
    id: "paper",
    name: "Paper & Ink",
    blurb: "Bright daylight white with a cobalt ink accent.",
    swatch: ["#F6F7FA", "#FFFFFF", "#2F55D4"],
  },
  {
    id: "sand",
    name: "Warm Sand",
    blurb: "Desert clay and terracotta with an olive edge.",
    swatch: ["#F9EFDE", "#FFFAF1", "#B4562A"],
  },
  {
    id: "citrus",
    name: "Citrus Grove",
    blurb: "Lime, leaf green and a squeeze of tangerine.",
    swatch: ["#F7F8E6", "#FFFFF3", "#2E9455"],
  },
  {
    id: "midnight",
    name: "Midnight Electric",
    blurb: "Deep indigo lit by electric azure and violet.",
    swatch: ["#0C1030", "#1A1F4A", "#4E93FF"],
  },
  {
    id: "nord",
    name: "Nord Ice",
    blurb: "Cool slate with glacier blue and aurora teal.",
    swatch: ["#333B4C", "#414C60", "#79CEE8"],
  },
  {
    id: "forest",
    name: "Deep Forest",
    blurb: "Evergreen dark with vivid mint and teal.",
    swatch: ["#0A1B14", "#122A20", "#3FD98B"],
  },
  {
    id: "gold",
    name: "Black Gold",
    blurb: "Near-black lacquer with molten brass and bronze.",
    swatch: ["#0B0906", "#1A140D", "#F0B93C"],
  },
  {
    id: "ember",
    name: "Ember",
    blurb: "Smoked charcoal cut by hot orange and coal red.",
    swatch: ["#170A06", "#2A130C", "#FF7A34"],
  },
  {
    id: "oxblood",
    name: "Oxblood",
    blurb: "Deep wine leather with a crimson signal.",
    swatch: ["#170709", "#2A0F12", "#D6404A"],
  },
  {
    id: "orchid",
    name: "Orchid",
    blurb: "Plum night with hot magenta and violet.",
    swatch: ["#170B1B", "#281435", "#E37DEC"],
  },
  {
    id: "vapor",
    name: "Vapor Dream",
    blurb: "Retro sunset pink over cool synth teal.",
    swatch: ["#1A1030", "#241847", "#FF83B8"],
  },
  {
    id: "cyberpunk",
    name: "Cyberpunk",
    blurb: "Neon magenta and cyan on midnight violet.",
    swatch: ["#0B0420", "#1A0A3A", "#FF3FD8"],
  },
  {
    id: "terminal",
    name: "Retro Terminal",
    blurb: "Phosphor green on CRT black, amber highlights.",
    swatch: ["#03110A", "#061C10", "#3BFF66"],
  },
];

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === "string" && THEMES.some((t) => t.id === value);
}

/** Signed-in app defaults to the calmer matte palette until a user picks one. */
export const APP_DEFAULT_THEME: ThemeId = "carbon";

/** Palette shown to signed-out visitors; the admin panel can change this. */
let publicDefaultTheme: ThemeId = DEFAULT_THEME;

export function setPublicDefaultTheme(id: ThemeId) {
  publicDefaultTheme = id;
}

export function getPublicDefaultTheme(): ThemeId {
  return publicDefaultTheme;
}

/** Which palette applies when the visitor has never chosen one. */
export function defaultThemeForPath(pathname: string): ThemeId {
  return pathname.startsWith("/app") ? APP_DEFAULT_THEME : publicDefaultTheme;
}

export function applyTheme(id: ThemeId) {
  const root = document.documentElement;
  if (id === DEFAULT_THEME) root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", id);
}

/** Runs before paint in the document head so themes never flash. */
export function themeBootScript(publicDefault: ThemeId = DEFAULT_THEME) {
  return `(function(){try{var k=${JSON.stringify(THEME_STORAGE_KEY)};var t=localStorage.getItem(k);if(!t){t=location.pathname.indexOf('/app')===0?${JSON.stringify(APP_DEFAULT_THEME)}:${JSON.stringify(publicDefault)};}if(t&&t!==${JSON.stringify(DEFAULT_THEME)})document.documentElement.setAttribute('data-theme',t);}catch(e){}})();`;
}

export const THEME_BOOT_SCRIPT = themeBootScript();
